import {
	type ChangeEventHandler,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import { useAsyncState } from "~/client/hooks";
import { mergeRefs } from "~/shared/utils";

import { useInput, getNameFromLabel } from "../Input";

import type { TextInputProps, TextInputDependencies } from "./TextInput.d";

/**
 * @function useTextInput
 * @param {TextInputDependencies} props
 * @returns {TextInputProps}
 */
export const useTextInput = ({
	initialValue = "",
	type = "text",
	validate,
	serverError,
	isRequired = false,
	label,
	syncToSource,
	onBlur: onBlurDep,
	onChange,
	"data-testid": dataId,
	name,
	...props
}: TextInputDependencies = {}): TextInputProps => {
	const [value, setValue] = useAsyncState(initialValue, syncToSource);
	const [selection, setSelection] = useState<number | null>(null);
	const ref = useRef<HTMLInputElement>(null);

	const { error, onBlur, onFocus } = useInput({
		"data-testid": dataId,
		value,
		validate,
		serverError,
		onBlur: onBlurDep,
	});

	const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
		setSelection(e.target.selectionStart);

		onChange?.(e);
		setValue(e.target.value);
	};

	useLayoutEffect(() => {
		if (type === "email") return;

		ref.current?.setSelectionRange(selection, selection);
	}, [type, selection]);

	return {
		...props,
		"data-testid": dataId,
		ref: mergeRefs(props.ref, ref),
		onBlur,
		onFocus,
		error,
		type,
		value,
		onChange: handleChange,
		name: name || getNameFromLabel(label),
		required: isRequired,
	};
};
